import { useState, useEffect, useRef } from 'react';
import { X, Search, Camera, MapPin, Home, User, Building, Mail, Phone, Briefcase, Upload, Check, AlertCircle, Loader2, CheckCircle, Save } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useOCR } from '../../hooks/useOCR';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import AddressAutocomplete from '../AddressAutocomplete';
import { getAllPeople } from '../../services/apiService';
import { addPeopleAsContact, addUserContact, getUserContacts, findPeopleForContact } from '../../services/userContactsService';
import { uploadFile, DOCUMENT_TYPES } from '../../services/filesService';
import ContactComparisonModal from './ContactComparisonModal';
import Button from '../ui/Button';

/**
 * Modal for adding a new contact
 * Features:
 * - Search existing people first
 * - Tab 1: Business card OCR scan
 * - Tab 2: Optional info (home needs, access codes)
 * - Tab 3: Address with Google Maps autocomplete
 */
const AddContactModal = ({ isOpen, onClose, currentUser, onContactAdded }) => {
    const { t, language } = useTranslation();
    const fileInputRef = useRef(null);

    // Load Google Maps API
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    const isGoogleMapsLoaded = useGoogleMaps(googleMapsApiKey);

    // State
    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
    const [existingContactIds, setExistingContactIds] = useState(new Set());

    // State for comparison modal
    const [showComparisonModal, setShowComparisonModal] = useState(false);
    const [foundPeople, setFoundPeople] = useState(null);

    // Home needs options (trades/professions)
    const homeNeedsOptions = {
        electrician: language === 'fr' ? 'Électricien' : 'Electrician',
        plumber: language === 'fr' ? 'Plombier' : 'Plumber',
        painter: language === 'fr' ? 'Peintre' : 'Painter',
        carpenter: language === 'fr' ? 'Menuisier' : 'Carpenter',
        roofing: language === 'fr' ? 'Couvreur' : 'Roofer',
        heating: language === 'fr' ? 'Chauffagiste' : 'Heating Specialist',
        gardener: language === 'fr' ? 'Jardinier' : 'Gardener',
        cleaner: language === 'fr' ? 'Nettoyage' : 'Cleaning',
        renovation: language === 'fr' ? 'Rénovation' : 'Renovation',
        other: language === 'fr' ? 'Autre' : 'Other'
    };

    // Form data
    const [formData, setFormData] = useState({
        // Basic info (from OCR or manual)
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gsm: '',
        company: '',
        position: '',
        // Optional info (Tab 2)
        homeNeeds: [],
        accessCode: '',
        notes: '',
        // Address (Tab 3)
        googleAddress: '',
        latitude: null,
        longitude: null
    });

    // OCR translations
    const ocrTranslations = {
        ocr: {
            processing: language === 'fr' ? 'Analyse en cours...' : 'Processing...',
            success: (count) => language === 'fr' ? `${count} champs extraits` : `${count} fields extracted`,
            noInfo: language === 'fr' ? 'Aucune information extraite' : 'No information extracted',
            error: language === 'fr' ? 'Erreur lors de l\'analyse' : 'Error during analysis',
            unavailable: language === 'fr' ? 'OCR non disponible' : 'OCR unavailable',
            invalidImage: language === 'fr' ? 'Fichier image invalide' : 'Invalid image file'
        }
    };

    // OCR hook
    const { isProcessing, imagePreview, message: ocrMessage, progress, processImage, clearImage, setMessage: setOcrMessage } = useOCR(language, ocrTranslations);

    // Load existing contacts and reset form when modal opens
    useEffect(() => {
        if (isOpen && currentUser?.id) {
            // Load existing contacts to mark them
            const loadExistingContacts = async () => {
                try {
                    const contacts = await getUserContacts(currentUser.id);
                    // Extract peopleuuids (the contact person's UUID)
                    const ids = new Set(contacts.map(c => c.peopleuuid).filter(Boolean));
                    setExistingContactIds(ids);
                    console.log('Existing contact peopleuuids:', [...ids]);
                } catch (error) {
                    console.error('Error loading existing contacts:', error);
                }
            };
            loadExistingContacts();

            // Reset form
            setActiveTab(0);
            setSearchQuery('');
            setSearchResults([]);
            setShowResults(false);
            setSelectedFile(null);
            setSaveMessage({ type: '', text: '' });
            setShowComparisonModal(false);
            setFoundPeople(null);
            clearImage();
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                gsm: '',
                company: '',
                position: '',
                homeNeeds: [],
                accessCode: '',
                notes: '',
                googleAddress: '',
                latitude: null,
                longitude: null
            });
        }
    }, [isOpen, currentUser?.id]);

    // Search existing people
    const handleSearch = async (query) => {
        setSearchQuery(query);

        if (query.length < 2) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }

        setIsSearching(true);
        setShowResults(true);

        try {
            // Use keyword parameter (API expects 'keyword' not 'search')
            const results = await getAllPeople({ keyword: query, limit: 10 });
            setSearchResults(results || []);
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Check if a person is already a contact (by peopleuuid)
    const isAlreadyContact = (person) => {
        const peopleuuid = person.peopleUuid || person.peopleuuid || person.PEOPLEUUID;
        return peopleuuid && existingContactIds.has(peopleuuid);
    };

    // Add existing person as contact
    const handleAddExistingContact = async (person) => {
        if (!currentUser?.id) return;

        setIsSaving(true);
        try {
            const result = await addPeopleAsContact(currentUser.id, person);
            if (result) {
                setSaveMessage({ type: 'success', text: language === 'fr' ? 'Contact ajouté avec succès!' : 'Contact added successfully!' });
                onContactAdded?.(result);
                // Dispatch custom event for MyContacts to refresh
                window.dispatchEvent(new CustomEvent('contactAdded', { detail: result }));
                setTimeout(() => onClose(), 1500);
            } else {
                setSaveMessage({ type: 'error', text: language === 'fr' ? 'Erreur lors de l\'ajout' : 'Error adding contact' });
            }
        } catch (error) {
            setSaveMessage({ type: 'error', text: error.message });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle file selection for business card
    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setSelectedFile(file);

        // Process with OCR
        const extractedData = await processImage(file);

        if (extractedData) {
            setFormData(prev => ({
                ...prev,
                firstName: extractedData.firstName || prev.firstName,
                lastName: extractedData.lastName || prev.lastName,
                email: extractedData.email || prev.email,
                phone: extractedData.phone || prev.phone,
                gsm: extractedData.gsm || extractedData.mobile || prev.gsm,
                company: extractedData.company || prev.company,
                position: extractedData.position || extractedData.title || prev.position,
                googleAddress: extractedData.address || prev.googleAddress
            }));
        }
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Handle address coordinates
    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    };

    // Handle address components from Google
    const handleAddressComponentsChange = (components) => {
        // Components contain: number, street, zip, city, country, countryCode
        console.log('Address components:', components);
    };

    // Save new contact - check if people exists first
    const handleSave = async () => {
        if (!currentUser?.id) return;

        // Validate minimum required fields
        if (!formData.firstName && !formData.lastName && !formData.email) {
            setSaveMessage({
                type: 'error',
                text: language === 'fr' ? 'Au moins un nom ou email requis' : 'At least a name or email required'
            });
            return;
        }

        // Need at least firstName AND lastName for people search
        if (!formData.firstName || !formData.lastName) {
            // No search possible, create as private contact directly
            await createPrivateContact();
            return;
        }

        // Need at least one of email, phone, or gsm for people search
        if (!formData.email && !formData.phone && !formData.gsm) {
            // No search possible, create as private contact directly
            await createPrivateContact();
            return;
        }

        setIsSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            // Search for existing people with matching criteria
            const existingPeople = await findPeopleForContact({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                gsm: formData.gsm
            });

            if (existingPeople) {
                // People found - open comparison modal
                console.log('Existing people found:', existingPeople);
                setFoundPeople(existingPeople);
                setShowComparisonModal(true);
                setIsSaving(false);
            } else {
                // No people found - create as private contact
                await createPrivateContact();
            }
        } catch (error) {
            console.error('Save error:', error);
            setSaveMessage({
                type: 'error',
                text: error.message || (language === 'fr' ? 'Erreur lors de la recherche' : 'Error during search')
            });
            setIsSaving(false);
        }
    };

    // Create private contact (no peopleuuid)
    const createPrivateContact = async () => {
        setIsSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            // Create contact without peopleuuid (private contact)
            const contactData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                gsm: formData.gsm,
                company: formData.company,
                position: formData.position,
                googleAddress: formData.googleAddress,
                latitude: formData.latitude,
                longitude: formData.longitude,
                homeNeeds: formData.homeNeeds,
                accessCode: formData.accessCode,
                notes: formData.notes
                // No peopleuuid = private CONTACT
            };

            const contactResult = await addUserContact(currentUser.id, contactData);

            if (contactResult) {
                // Upload business card if present
                if (selectedFile && contactResult.localuuid) {
                    try {
                        await uploadFile(selectedFile, {
                            path: `contacts/${contactResult.localuuid}/`,
                            fileType: DOCUMENT_TYPES.BUSINESS_CARD,
                            entityType: 'contacts',
                            entityId: contactResult.id,
                            entityUuid: contactResult.localuuid,
                            uploadedById: currentUser.id,
                            description: 'Business card'
                        });
                        console.log('Business card uploaded');
                    } catch (uploadError) {
                        console.error('Error uploading business card:', uploadError);
                    }
                }

                setSaveMessage({
                    type: 'success',
                    text: language === 'fr' ? 'Contact créé avec succès!' : 'Contact created successfully!'
                });
                onContactAdded?.(contactResult);
                // Dispatch custom event for MyContacts to refresh
                window.dispatchEvent(new CustomEvent('contactAdded', { detail: contactResult }));
                setTimeout(() => onClose(), 1500);
            } else {
                throw new Error('Failed to create contact');
            }
        } catch (error) {
            console.error('Create contact error:', error);
            setSaveMessage({
                type: 'error',
                text: error.message || (language === 'fr' ? 'Erreur lors de la création' : 'Error creating contact')
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle confirmation from comparison modal
    const handleComparisonConfirm = async (mergedData) => {
        setShowComparisonModal(false);
        setIsSaving(true);
        setSaveMessage({ type: '', text: '' });

        try {
            // Create contact with peopleuuid (PUBLIC contact)
            const contactData = {
                peopleuuid: mergedData.peopleuuid,
                firstName: mergedData.firstName,
                lastName: mergedData.lastName,
                email: mergedData.email,
                phone: mergedData.phone,
                gsm: mergedData.gsm,
                company: mergedData.company,
                position: mergedData.position,
                googleAddress: mergedData.googleAddress,
                latitude: formData.latitude,
                longitude: formData.longitude,
                homeNeeds: formData.homeNeeds,
                accessCode: formData.accessCode,
                notes: formData.notes
            };

            const contactResult = await addUserContact(currentUser.id, contactData);

            if (contactResult) {
                // Upload business card if present
                if (selectedFile && contactResult.localuuid) {
                    try {
                        await uploadFile(selectedFile, {
                            path: `contacts/${contactResult.localuuid}/`,
                            fileType: DOCUMENT_TYPES.BUSINESS_CARD,
                            entityType: 'contacts',
                            entityId: contactResult.id,
                            entityUuid: contactResult.localuuid,
                            uploadedById: currentUser.id,
                            description: 'Business card'
                        });
                    } catch (uploadError) {
                        console.error('Error uploading business card:', uploadError);
                    }
                }

                setSaveMessage({
                    type: 'success',
                    text: language === 'fr' ? 'Contact public créé avec succès!' : 'Public contact created successfully!'
                });
                onContactAdded?.(contactResult);
                // Dispatch custom event for MyContacts to refresh
                window.dispatchEvent(new CustomEvent('contactAdded', { detail: contactResult }));
                setTimeout(() => onClose(), 1500);
            } else {
                throw new Error('Failed to create contact');
            }
        } catch (error) {
            console.error('Create public contact error:', error);
            setSaveMessage({
                type: 'error',
                text: error.message || (language === 'fr' ? 'Erreur lors de la création' : 'Error creating contact')
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Tab definitions
    const tabs = [
        { id: 0, icon: Camera, label: language === 'fr' ? 'Carte de visite' : 'Business Card' },
        { id: 1, icon: Home, label: language === 'fr' ? 'Infos optionnelles' : 'Optional Info' },
        { id: 2, icon: MapPin, label: language === 'fr' ? 'Adresse' : 'Address' }
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-cabaza-purple to-cabaza-pink p-4 flex items-center justify-between">
                    <h2 className="text-white text-lg font-semibold">
                        {language === 'fr' ? 'Ajouter un contact' : 'Add Contact'}
                    </h2>
                    <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded-full transition">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search existing people */}
                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            placeholder={language === 'fr' ? 'Rechercher un contact existant...' : 'Search existing contact...'}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                        />
                        {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cabaza-purple animate-spin" />
                        )}
                    </div>

                    {/* Search results */}
                    {showResults && searchResults.length > 0 && (
                        <div className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                            {searchResults.map((person) => {
                                const personKey = person.peopleUuid || person.peopleuuid || person.id || person.ID;
                                const alreadyContact = isAlreadyContact(person);
                                return (
                                <div
                                    key={personKey}
                                    className={`flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0 ${
                                        alreadyContact
                                            ? 'bg-green-50 cursor-default'
                                            : 'hover:bg-gray-50 cursor-pointer'
                                    }`}
                                    onClick={() => !alreadyContact && handleAddExistingContact(person)}
                                >
                                    <div className="flex items-center gap-3">
                                        {alreadyContact && (
                                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                        )}
                                        <div>
                                            <div className={`font-medium ${alreadyContact ? 'text-green-700' : 'text-gray-900'}`}>
                                                {person.firstName} {person.lastName}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {person.email || person.company || ''}
                                            </div>
                                        </div>
                                    </div>
                                    {alreadyContact ? (
                                        <span className="text-xs text-green-600 font-medium px-2 py-1 bg-green-100 rounded-full">
                                            {language === 'fr' ? 'Déjà contact' : 'Already contact'}
                                        </span>
                                    ) : (
                                        <button className="text-cabaza-purple hover:bg-cabaza-purple/10 p-2 rounded-lg transition">
                                            <Check className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    )}

                    {showResults && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                        <div className="mt-2 text-sm text-gray-500 text-center py-2">
                            {language === 'fr' ? 'Aucun contact trouvé. Créez-en un nouveau ci-dessous.' : 'No contact found. Create a new one below.'}
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition ${
                                activeTab === tab.id
                                    ? 'text-cabaza-purple border-b-2 border-cabaza-purple bg-purple-50'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className="p-4 overflow-y-auto max-h-[50vh]">
                    {/* Tab 0: Business Card OCR */}
                    {activeTab === 0 && (
                        <div className="space-y-4">
                            {/* Upload area */}
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-cabaza-purple transition"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />

                                {imagePreview ? (
                                    <div className="space-y-3">
                                        <img
                                            src={imagePreview}
                                            alt="Business card"
                                            className="max-h-40 mx-auto rounded-lg shadow"
                                        />
                                        {isProcessing && (
                                            <div className="flex items-center justify-center gap-2 text-cabaza-purple">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>{ocrMessage.text} ({progress}%)</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <Upload className="w-12 h-12 mx-auto text-gray-400" />
                                        <p className="mt-2 text-sm text-gray-600">
                                            {language === 'fr'
                                                ? 'Cliquez ou glissez une photo de carte de visite'
                                                : 'Click or drag a business card photo'}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            {language === 'fr' ? 'JPG, PNG, GIF (max 5MB)' : 'JPG, PNG, GIF (max 5MB)'}
                                        </p>
                                    </>
                                )}
                            </div>

                            {/* OCR message */}
                            {ocrMessage.text && !isProcessing && (
                                <div className={`p-3 rounded-lg text-sm ${
                                    ocrMessage.type === 'success' ? 'bg-green-50 text-green-700' :
                                    ocrMessage.type === 'error' ? 'bg-red-50 text-red-700' :
                                    ocrMessage.type === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                                    'bg-blue-50 text-blue-700'
                                }`}>
                                    {ocrMessage.text}
                                </div>
                            )}

                            {/* Basic info fields */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                        <User className="w-4 h-4 mr-2 text-cabaza-purple" />
                                        {language === 'fr' ? 'Prénom' : 'First Name'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.firstName}
                                        onChange={(e) => handleFieldChange('firstName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                        <User className="w-4 h-4 mr-2 text-cabaza-purple" />
                                        {language === 'fr' ? 'Nom' : 'Last Name'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.lastName}
                                        onChange={(e) => handleFieldChange('lastName', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <Mail className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleFieldChange('email', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                        <Phone className="w-4 h-4 mr-2 text-cabaza-purple" />
                                        {language === 'fr' ? 'Téléphone' : 'Phone'}
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                        <Phone className="w-4 h-4 mr-2 text-cabaza-purple" />
                                        GSM
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.gsm}
                                        onChange={(e) => handleFieldChange('gsm', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                        <Building className="w-4 h-4 mr-2 text-cabaza-purple" />
                                        {language === 'fr' ? 'Société' : 'Company'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.company}
                                        onChange={(e) => handleFieldChange('company', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                        <Briefcase className="w-4 h-4 mr-2 text-cabaza-purple" />
                                        {language === 'fr' ? 'Fonction' : 'Position'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.position}
                                        onChange={(e) => handleFieldChange('position', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab 1: Optional Info */}
                    {activeTab === 1 && (
                        <div className="space-y-4">
                            {/* Home Needs Checkboxes */}
                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                                    <Home className="w-4 h-4 mr-2 text-cabaza-purple" />
                                    {language === 'fr' ? 'Besoins pour la maison' : 'Home Needs'}
                                </label>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {Object.entries(homeNeedsOptions).map(([key, label]) => (
                                        <label key={key} className="flex items-center space-x-2 cursor-pointer group">
                                            <input
                                                type="checkbox"
                                                checked={formData.homeNeeds?.includes(key) || false}
                                                onChange={(e) => {
                                                    const newHomeNeeds = e.target.checked
                                                        ? [...(formData.homeNeeds || []), key]
                                                        : (formData.homeNeeds || []).filter(need => need !== key);
                                                    handleFieldChange('homeNeeds', newHomeNeeds);
                                                }}
                                                className="w-4 h-4 text-cabaza-purple border-gray-300 rounded focus:ring-cabaza-purple focus:ring-2"
                                            />
                                            <span className="text-sm text-gray-700 group-hover:text-cabaza-purple transition-colors">
                                                {label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <span className="w-4 h-4 mr-2 text-cabaza-purple font-bold text-xs">123</span>
                                    {language === 'fr' ? 'Code d\'accès' : 'Access Code'}
                                </label>
                                <input
                                    type="text"
                                    value={formData.accessCode}
                                    onChange={(e) => handleFieldChange('accessCode', e.target.value)}
                                    placeholder={language === 'fr' ? 'Code portail, digicode...' : 'Gate code, keypad...'}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                />
                            </div>

                            <div>
                                <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                    <span className="w-4 h-4 mr-2 text-cabaza-purple">📝</span>
                                    Notes
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                                    rows={4}
                                    placeholder={language === 'fr' ? 'Notes personnelles...' : 'Personal notes...'}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple resize-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Tab 2: Address */}
                    {activeTab === 2 && (
                        <div className="space-y-4">
                            {isGoogleMapsLoaded ? (
                                <AddressAutocomplete
                                    label={language === 'fr' ? 'Adresse' : 'Address'}
                                    value={formData.googleAddress}
                                    onChange={(field, value) => handleFieldChange('googleAddress', value)}
                                    onCoordinatesChange={handleCoordinatesChange}
                                    onAddressComponentsChange={handleAddressComponentsChange}
                                    apiKey={googleMapsApiKey}
                                />
                            ) : (
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                                        <MapPin className="w-4 h-4 mr-2 text-cabaza-purple" />
                                        {language === 'fr' ? 'Adresse' : 'Address'}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.googleAddress}
                                        onChange={(e) => handleFieldChange('googleAddress', e.target.value)}
                                        placeholder={language === 'fr' ? 'Entrez une adresse...' : 'Enter an address...'}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        {language === 'fr' ? 'Chargement de Google Maps...' : 'Loading Google Maps...'}
                                    </p>
                                </div>
                            )}

                            {formData.latitude && formData.longitude && (
                                <div className="text-sm text-gray-500 flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-green-500" />
                                    {language === 'fr' ? 'Coordonnées GPS enregistrées' : 'GPS coordinates saved'}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    {/* Save message */}
                    {saveMessage.text && (
                        <div className={`mb-3 p-3 rounded-lg text-sm flex items-center gap-2 ${
                            saveMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                            {saveMessage.type === 'success' ? (
                                <Check className="w-4 h-4" />
                            ) : (
                                <AlertCircle className="w-4 h-4" />
                            )}
                            {saveMessage.text}
                        </div>
                    )}

                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            fullWidth
                        >
                            {language === 'fr' ? 'Annuler' : 'Cancel'}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isProcessing}
                            loading={isSaving}
                            icon={Save}
                            fullWidth
                        >
                            {language === 'fr' ? 'Enregistrer' : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Comparison Modal */}
            <ContactComparisonModal
                isOpen={showComparisonModal}
                onClose={() => setShowComparisonModal(false)}
                formData={formData}
                peopleData={foundPeople}
                onConfirm={handleComparisonConfirm}
            />
        </div>
    );
};

export default AddContactModal;
